/**
 * GateButtons — iter84.56.7
 *
 * Shared three-button cluster (Approve / Waive / Deny) for runbook
 * checklist items. Distributed across individual Release Ops cards so
 * each functional surface (Parity Sweep, Deployment Readiness, Values
 * Engine, UAT, etc.) carries its own gate disposition inline rather
 * than collecting them all in a centralized RunbookGatePanel.
 *
 * Props:
 *   - promotionId: string | null   (no promotion → buttons disabled)
 *   - section: "pre_deploy" | "post_deploy"
 *   - itemId: string               (e.g. "ci_green", "values_check_preview")
 *   - label: string                (human-readable gate name shown in pill)
 *   - required?: boolean           (renders REQUIRED tag, default true)
 *   - state?: { state, waiver_reason, by_email, at }
 *   - onUpdated?: (refreshedPromotion) => void
 *   - disabled?: boolean
 *   - promotionStatus?: string     (when "finalized"/"cancelled" → readonly)
 *
 * Hits POST /api/release-ops/promotion/{id}/check.
 */
import { useState } from "react";
import {
    CheckCircle2,
    AlertTriangle,
    XCircle,
    RotateCcw,
    Loader2,
    Pencil,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const STATUS_PRESENTATION = {
    complete: {
        label: "Approved",
        cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
    },
    waived: {
        label: "Waived",
        cls: "border-amber-500/40 bg-amber-500/10 text-amber-300",
    },
    denied: {
        label: "Denied",
        cls: "border-rose-500/40 bg-rose-500/10 text-rose-300",
    },
    pending: {
        label: "Pending",
        cls: "border-zinc-700 bg-zinc-800/60 text-zinc-400",
    },
};

export function GateButtons({
    promotionId,
    section,
    itemId,
    label,
    required = true,
    state,
    onUpdated,
    disabled,
    promotionStatus,
}) {
    const [busy, setBusy] = useState(false);
    const [reasoning, setReasoning] = useState(null); // null | "waive" | "deny"
    const [reason, setReason] = useState("");

    const current = state?.state || "pending";
    const isComplete = current === "complete";
    const isWaived = current === "waived";
    const isDenied = current === "denied";
    const isTerminal = promotionStatus === "finalized" || promotionStatus === "cancelled";
    const noPromotion = !promotionId;

    const submit = async (newState, waiverReason) => {
        if (noPromotion) {
            toast.error("Start a promotion in Phase 0 first");
            return;
        }
        setBusy(true);
        try {
            const r = await api.runbookUpdateCheck(promotionId, {
                item_id: itemId,
                section,
                state: newState,
                waiver_reason: waiverReason || null,
            });
            onUpdated?.(r);
            const verb = newState === "reset" ? "reset" : newState;
            toast.success(`${label}: ${verb}`);
            setReasoning(null);
            setReason("");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to update gate");
        } finally {
            setBusy(false);
        }
    };

    const presentation = STATUS_PRESENTATION[current] || STATUS_PRESENTATION.pending;

    if (reasoning) {
        const verbCap = reasoning === "waive" ? "Waive" : reasoning === "deny" ? "Deny" : "Edit reason";
        const targetState =
            reasoning === "waive"
                ? "waived"
                : reasoning === "deny"
                    ? "denied"
                    : (isWaived ? "waived" : "denied"); // edit-reason → preserve current state
        return (
            <div
                data-testid={`gate-${itemId}-reason-form`}
                className="mt-2 p-2 border-l-4 border-amber-500/50 bg-zinc-950/60 space-y-2"
            >
                <p className="text-[10px] font-bold uppercase tracking-widest text-amber-300">
                    {verbCap} · why?
                </p>
                <input
                    type="text"
                    autoFocus
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder={
                        reasoning === "waive"
                            ? "Reason for waiving (audit trail)…"
                            : "Reason for marking denied (audit trail)…"
                    }
                    data-testid={`gate-${itemId}-reason-input`}
                    className="brutal-input w-full text-xs"
                />
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={() => {
                            if (!reason.trim()) {
                                toast.error("Reason required");
                                return;
                            }
                            submit(targetState, reason.trim());
                        }}
                        disabled={busy || !reason.trim()}
                        data-testid={`gate-${itemId}-${reasoning}-confirm-btn`}
                        className="brutal-btn-ghost text-[10px] py-1 px-2 disabled:opacity-50"
                    >
                        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                        Confirm {verbCap}
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            setReasoning(null);
                            setReason("");
                        }}
                        disabled={busy}
                        data-testid={`gate-${itemId}-${reasoning}-cancel-btn`}
                        className="brutal-btn-ghost text-[10px] py-1 px-2"
                    >
                        Back
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div
            data-testid={`gate-buttons-${itemId}`}
            data-state={current}
            className="mt-2 flex items-center justify-between gap-2 flex-wrap p-2 border-l-4 border-cyber/40 bg-zinc-950/40"
        >
            <div className="flex items-center gap-2 min-w-0 flex-wrap">
                <span
                    className={`pill text-[9px] font-bold uppercase border ${presentation.cls}`}
                    data-testid={`gate-${itemId}-status-pill`}
                >
                    {presentation.label}
                </span>
                <span className="text-[11px] text-zinc-200 truncate" title={label}>
                    {label}
                </span>
                {required && (
                    <span className="text-[9px] text-rose-400 font-bold uppercase tracking-widest">
                        REQUIRED
                    </span>
                )}
                {state?.waiver_reason && (
                    <span
                        className="text-[10px] text-amber-400 italic truncate max-w-[40ch]"
                        title={state.waiver_reason}
                    >
                        "{state.waiver_reason}"
                    </span>
                )}
                {state?.by_email && (
                    <span className="text-[9px] text-zinc-600 truncate">
                        by {state.by_email}
                    </span>
                )}
            </div>
            <div className="flex items-center gap-1 shrink-0">
                {noPromotion && (
                    <span className="text-[10px] text-zinc-500 italic">
                        Start a promotion in Phase 0 to gate this
                    </span>
                )}
                {!noPromotion && isTerminal && (
                    <span className="text-[10px] text-zinc-500 italic">
                        Promotion {promotionStatus} — gate frozen
                    </span>
                )}
                {!noPromotion && !isTerminal && (isWaived || isDenied) && (
                    <button
                        type="button"
                        onClick={() => {
                            setReason(state?.waiver_reason || "");
                            setReasoning("edit");
                        }}
                        disabled={busy || disabled}
                        data-testid={`gate-${itemId}-edit-reason-btn`}
                        title="Edit reason (e.g. replace an auto-generated one with your own commentary)"
                        className="brutal-btn-ghost text-[10px] py-1 px-2 disabled:opacity-50"
                    >
                        <Pencil className="w-3 h-3" />
                        Edit reason
                    </button>
                )}
                {!noPromotion && !isTerminal && (isComplete || isWaived || isDenied) && (
                    <button
                        type="button"
                        onClick={() => submit("reset")}
                        disabled={busy || disabled}
                        data-testid={`gate-${itemId}-reset-btn`}
                        title="Reset to pending"
                        className="brutal-btn-ghost text-[10px] py-1 px-2 disabled:opacity-50"
                    >
                        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                        Reset
                    </button>
                )}
                {!noPromotion && !isTerminal && !isComplete && (
                    <button
                        type="button"
                        onClick={() => submit("complete")}
                        disabled={busy || disabled}
                        data-testid={`gate-${itemId}-approve-btn`}
                        className="brutal-btn text-[10px] py-1 px-2 disabled:opacity-50 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                    >
                        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                        Approve
                    </button>
                )}
                {!noPromotion && !isTerminal && !isWaived && (
                    <button
                        type="button"
                        onClick={() => setReasoning("waive")}
                        disabled={busy || disabled}
                        data-testid={`gate-${itemId}-waive-btn`}
                        className="brutal-btn-ghost text-[10px] py-1 px-2 text-amber-300 disabled:opacity-50"
                    >
                        <AlertTriangle className="w-3 h-3" />
                        Waive
                    </button>
                )}
                {!noPromotion && !isTerminal && !isDenied && (
                    <button
                        type="button"
                        onClick={() => setReasoning("deny")}
                        disabled={busy || disabled}
                        data-testid={`gate-${itemId}-deny-btn`}
                        className="brutal-btn-ghost text-[10px] py-1 px-2 text-rose-300 disabled:opacity-50"
                    >
                        <XCircle className="w-3 h-3" />
                        Deny
                    </button>
                )}
            </div>
        </div>
    );
}

export default GateButtons;
